import { zodResolver } from '@hookform/resolvers/zod';
import {
  IOfferingRes,
  IProduct,
  IRateReqSchemaUI,
  MinCommitConfigTypeEnum,
  OfferingTypesEnum,
  PercentOfTotalConfigTypeEnum,
  RateReqSchemaUI,
  RateTypeEnum,
  SubscriptionTimingEnum,
} from '@monetize/types/app';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../../../constants/offerings';
import { useFlags } from '../../../../../services/launchDarkly';
import { checkIfProductsHaveBuckets } from '../../../../../utils/rates';
import { getOfferingType } from '../../offeringUtils';
import RateForm from './RateForm';
import {
  getDefaultBillingFrequency,
  getDefaultUsageBillingFrequency,
  getProductTypeExistence,
} from './RatePriceUtils';

export interface RateFormRef {
  getRate: () => IRateReqSchemaUI;
}

export interface RateProps {
  id: string;
  products: IProduct[];
  type: OfferingTypesEnum;
  typeId: string;
  archivedOffering?: boolean;
  rateCloneData?: IRateReqSchemaUI;
  /**
   * Indicates whether the rate form is hidden
   * Retain all the component state but omit from the dom to avoid re-fetching data
   */
  isHidden?: boolean;
  clearRateCloneData: () => void;
  handleCloneRate: (data: IRateReqSchemaUI) => void;
  handleDirtyRateIds: (rateId: string, type: 'add' | 'remove') => void;
  onChange?: (rateId: string) => void;
  removeRateTempUUId: (savedTempRateId: string) => void;
  onRemove?: () => void;
  isReadOnly?: boolean;
  isExpandedAll?: boolean;
  offering?: IOfferingRes;
}

const RateFormProvider = forwardRef<RateFormRef, RateProps>(
  (
    {
      id,
      products,
      type,
      typeId,
      archivedOffering,
      rateCloneData,
      isHidden = false,
      clearRateCloneData,
      handleCloneRate,
      handleDirtyRateIds,
      onChange,
      removeRateTempUUId,
      onRemove,
      isReadOnly,
      isExpandedAll,
      offering,
    },
    ref,
  ) => {
    const { enableTokenCredits } = useFlags();
    const isClonedRate = !!id?.startsWith('clone-');
    const isClonedImportedRate = id?.startsWith('clone-import-');
    const {
      isOneTime,
      isMinCommit,
      isSubscription,
      isPercentOfTotal,
      isOnetimeToken,
    } = getOfferingType(type);
    const { isSubscriptionProductTypeExist, isUsageProductTypeExist } =
      getProductTypeExistence(products);

    const methods = useForm({
      resolver: zodResolver(RateReqSchemaUI),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        rateType: RateTypeEnum.CATALOG,
        billingFrequency: getDefaultBillingFrequency({
          isOneTime,
          isMinCommit,
          isPercentOfTotal,
          isSubscription,
          isSubscriptionProductTypeExist,
          isOnetimeToken,
        }),
        billingFrequencyInMonths: null,
        subscriptionTiming: isMinCommit
          ? SubscriptionTimingEnum.ARREARS
          : SubscriptionTimingEnum.ADVANCE,
        usageBillingFrequency: getDefaultUsageBillingFrequency({
          isMinCommit,
          isUsageProductTypeExist,
        }),
        minCommitConfig:
          type === OfferingTypesEnum.MIN_COMMIT
            ? { type: MinCommitConfigTypeEnum.USAGE_PRODUCTS }
            : null,
        percentOfTotalConfig:
          type === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL
            ? {
                type: PercentOfTotalConfigTypeEnum.ALL_ELIGIBLE,
                minimumTotal: null,
                maximumTotal: null,
              }
            : null,
        revenueChannels: ['SALES'],
      },
    });

    const { getValues, setValue, reset } = methods;
    const { name } = getValues();

    useImperativeHandle(ref, () => ({
      getRate: () =>
        ({
          ...getValues(),
          id,
        }) as IRateReqSchemaUI,
    }));

    useEffect(() => {
      if (!name && rateCloneData && isClonedRate) {
        reset(rateCloneData);

        // If rate is cloned from a different offering, then don't add "copy of" to the name
        if (!isClonedImportedRate) {
          const copyOfName = rateCloneData.name
            ? `Copy of ${rateCloneData.name}`
            : 'Copy Of ';
          //simple hack to make the form dirty
          setTimeout(() => {
            setValue('name', copyOfName, {
              shouldDirty: true,
            });
          });
        }

        clearRateCloneData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateCloneData, isClonedImportedRate, isClonedRate]);

    useEffect(() => {
      if (products.length > 0 && enableTokenCredits) {
        const hasBucketedProducts = checkIfProductsHaveBuckets(products);
        const defaultFrequency = getDefaultBillingFrequency({
          isOneTime,
          isMinCommit,
          isPercentOfTotal,
          isSubscription,
          isSubscriptionProductTypeExist,
          isOnetimeToken,
        });

        if (hasBucketedProducts) {
          const creditAllocationFrequency =
            getValues('options.tokenAllocationFrequencyInMonth') ??
            getValues('billingFrequencyInMonths') ??
            RATE_BILLING_FREQUENCY_MAP[defaultFrequency]().month;

          setValue(
            'options.tokenAllocationFrequencyInMonth',
            creditAllocationFrequency,
          );
        }
      }
    }, [products, enableTokenCredits]);

    if (isHidden) {
      return null;
    }

    return (
      <FormProvider {...methods}>
        <form
          style={{ width: '100%' }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <RateForm
            id={id}
            type={type}
            archivedOffering={archivedOffering}
            typeId={typeId}
            products={products}
            isClonedRate={isClonedRate}
            onChange={onChange}
            removeRateTempUUId={removeRateTempUUId}
            onRemove={onRemove}
            handleCloneRate={handleCloneRate}
            handleDirtyRateIds={handleDirtyRateIds}
            isReadOnly={isReadOnly}
            isSubscriptionProductTypeExist={isSubscriptionProductTypeExist}
            isSubscription={isSubscription}
            isExpandedAll={isExpandedAll}
            offering={offering}
          />
        </form>
      </FormProvider>
    );
  },
);

export default RateFormProvider;
